import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { HierarchyResponse, ProductSlug, ServiceSlug, TenantSlug } from "../api/client";
import { listFullHierarchy } from "../api/management";
import { useAuth } from "./AuthContext";
import type { Tenant, Product, Service } from "../api/types";
import type { TenantWithRole } from "../api/management";

export interface ProductWithTenant extends Product {
  tenantSlug: TenantSlug;
}

export interface ServiceWithParents extends Service {
  tenantSlug: TenantSlug;
  productSlug: ProductSlug;
}

interface HierarchyContextBase {
  tenants: TenantWithRole[];
  products: ProductWithTenant[];
  services: ServiceWithParents[];
  user: { id: string; email: string; username?: string | null; tenant_roles?: Record<string, string> } | null;
  smtpConfigured: boolean;
  loading: boolean;
  error: Error | null;
}

export interface HierarchySlugs {
  tenant: string | null;
  product: string | null;
  service: string | null;
}

export interface HierarchyContextValue extends HierarchyContextBase {
  hierarchy: HierarchyResponse | null;
}

const HierarchyContext = createContext<HierarchyContextBase | null>(null);

async function fetchFullHierarchy(signal?: AbortSignal) {
  const res = await listFullHierarchy(signal);
  const productsWithTenant: ProductWithTenant[] = res.products.map(
    ({ tenant_slug, ...p }) => ({ ...p, tenantSlug: tenant_slug }),
  );
  const servicesWithParents: ServiceWithParents[] = res.services.map(
    ({ tenant_slug, product_slug, ...s }) => ({
      ...s,
      tenantSlug: tenant_slug,
      productSlug: product_slug,
    }),
  );
  const user =
    res.user != null
      ? {
          id: res.user.id,
          email: res.user.email,
          username: res.user.username ?? null,
          tenant_roles: Object.fromEntries(
            (res.tenants ?? [])
              .filter((t): t is typeof t & { role: string } => !!t.role)
              .map((t) => [t.id, t.role]),
          ) as Record<string, string> | undefined,
        }
      : null;

  return {
    tenants: res.tenants,
    products: productsWithTenant,
    services: servicesWithParents,
    user,
    smtpConfigured: res.smtp_configured,
  };
}

function deriveHierarchy(
  tenants: TenantWithRole[],
  products: ProductWithTenant[],
  services: ServiceWithParents[],
  slugs: HierarchySlugs,
): HierarchyResponse | null {
  if (slugs.service) {
    const service = services.find((s) => s.slug === slugs.service);
    if (!service) return null;
    const product = products.find((p) => p.slug === service.productSlug);
    const tenant = tenants.find((t) => t.slug === service.tenantSlug);
    if (!product || !tenant) return null;
    return {
      tenant: { slug: tenant.slug, name: tenant.name },
      product: { slug: product.slug, name: product.name },
      service: { slug: service.slug, name: service.name },
    };
  }
  if (slugs.product) {
    const product = products.find((p) => p.slug === slugs.product);
    if (!product) return null;
    const tenant = tenants.find((t) => t.slug === product.tenantSlug);
    if (!tenant) return null;
    return {
      tenant: { slug: tenant.slug, name: tenant.name },
      product: { slug: product.slug, name: product.name },
    };
  }
  if (slugs.tenant) {
    const tenant = tenants.find((t) => t.slug === slugs.tenant);
    if (!tenant) return null;
    return { tenant: { slug: tenant.slug, name: tenant.name } };
  }
  return null;
}

export function HierarchyProvider({ children }: PropsWithChildren) {
  const { syncUserFromHierarchy, status } = useAuth();
  const query = useQuery({
    queryKey: ["hierarchy", "full"],
    queryFn: ({ signal }) => fetchFullHierarchy(signal),
    staleTime: 60_000,
    enabled: status === "authenticated",
  });

  useEffect(() => {
    if (query.data?.user) {
      syncUserFromHierarchy(query.data.user);
    }
  }, [query.data?.user, syncUserFromHierarchy]);

  const value = useMemo(
    () => ({
      tenants: query.data?.tenants ?? [],
      products: query.data?.products ?? [],
      services: query.data?.services ?? [],
      user: query.data?.user ?? null,
      smtpConfigured: query.data?.smtpConfigured ?? false,
      loading: query.isLoading,
      error: query.error instanceof Error ? query.error : null,
    }),
    [query.data, query.isLoading, query.error],
  );

  return (
    <HierarchyContext.Provider value={value}>{children}</HierarchyContext.Provider>
  );
}

export function useHierarchyContext(): HierarchyContextValue {
  const ctx = useContext(HierarchyContext);
  if (!ctx) {
    throw new Error("useHierarchyContext must be used within a HierarchyProvider");
  }
  const params = useParams();
  const slugs = useMemo<HierarchySlugs>(
    () => ({
      tenant: (typeof params.tenant === "string" ? params.tenant : null) as TenantSlug | null,
      product: (typeof params.product === "string" ? params.product : null) as ProductSlug | null,
      service: (typeof params.service === "string" ? params.service : null) as ServiceSlug | null,
    }),
    [params.tenant, params.product, params.service],
  );

  const hierarchy = useMemo(
    () =>
      deriveHierarchy(
        ctx.tenants,
        ctx.products,
        ctx.services,
        slugs
      ),
    [ctx.tenants, ctx.products, ctx.services, slugs],
  );

  return useMemo<HierarchyContextValue>(
    () => ({
      ...ctx,
      hierarchy,
    }),
    [ctx, hierarchy],
  );
}
